import { useCallback, useEffect, useState } from "react";
import { Check, Dot, KeyRound, Mail, Smartphone, TriangleAlert, UserRound, X, type LucideIcon } from "lucide-react";

import { api } from "../api/client";
import type { ActivityType, EmployeeActivityEntry } from "../types";

const DATETIME_FMT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const ICON: Record<string, { glyph: LucideIcon; tone: string }> = {
  welcome_email_sent: { glyph: Mail, tone: "text-pme-blue" },
  welcome_email_resent: { glyph: Mail, tone: "text-pme-blue" },
  welcome_email_failed: { glyph: X, tone: "text-red-600" },
  invite_resent: { glyph: Mail, tone: "text-pme-blue" },
  pin_reset: { glyph: KeyRound, tone: "text-amber-600" },
  pin_changed: { glyph: KeyRound, tone: "text-amber-600" },
  admin_pin_set: { glyph: KeyRound, tone: "text-amber-600" },
  admin_pin_changed: { glyph: KeyRound, tone: "text-amber-600" },
  passkey_registered: { glyph: Smartphone, tone: "text-green-600" },
  passkey_removed: { glyph: Smartphone, tone: "text-red-600" },
  device_bound: { glyph: Smartphone, tone: "text-green-600" },
  device_unbound: { glyph: Smartphone, tone: "text-gray-500" },
  profile_updated: { glyph: UserRound, tone: "text-gray-600" },
  role_changed: { glyph: UserRound, tone: "text-pme-blue" },
  deactivated: { glyph: TriangleAlert, tone: "text-amber-600" },
  reactivated: { glyph: Check, tone: "text-green-600" },
};

function iconFor(type: ActivityType): { glyph: LucideIcon; tone: string } {
  return ICON[type] ?? { glyph: Dot, tone: "text-gray-500" };
}

interface Props {
  employeeId: number;
}

export default function EmployeeTimeline({ employeeId }: Props) {
  const [entries, setEntries] = useState<EmployeeActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const limit = 20;

  const load = useCallback(
    async (offset: number) => {
      setLoading(true);
      try {
        const res = await api.get<{ activities: EmployeeActivityEntry[] }>(
          `/api/employees/${employeeId}/activities?limit=${limit}&offset=${offset}`,
        );
        if (offset === 0) {
          setEntries(res.activities);
        } else {
          setEntries((prev) => [...prev, ...res.activities]);
        }
        setHasMore(res.activities.length === limit);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [employeeId],
  );

  useEffect(() => {
    void load(0);
  }, [load]);

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        Activity Timeline
      </h2>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {entries.length === 0 && !loading && (
        <p className="text-sm text-gray-400">No activity recorded yet.</p>
      )}

      <ol className="relative border-l-2 border-gray-200 pl-5 space-y-4">
        {entries.map((e) => {
          const { glyph: Glyph, tone } = iconFor(e.activity_type);
          return (
            <li key={e.id} className="relative">
              <span
                aria-hidden
                className={[
                  "absolute -left-[1.65rem] flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm ring-2 ring-gray-200",
                  tone,
                ].join(" ")}
              >
                <Glyph size={16} strokeWidth={1.75} aria-hidden />
              </span>
              <div>
                <p className="text-sm text-gray-900">{e.description}</p>
                <p className="text-[11px] text-gray-500">
                  {DATETIME_FMT.format(new Date(e.created_at))}
                  {e.actor_name && ` · by ${e.actor_name}`}
                </p>
                {e.metadata && Object.keys(e.metadata).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[10px] text-gray-400 hover:text-gray-600">
                      details
                    </summary>
                    <pre className="mt-1 rounded bg-gray-50 p-2 text-[10px] text-gray-600 overflow-x-auto">
                      {JSON.stringify(e.metadata, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {hasMore && entries.length > 0 && (
        <button
          type="button"
          disabled={loading}
          onClick={() => void load(entries.length)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs disabled:opacity-60"
        >
          {loading ? "Loading..." : "Load more"}
        </button>
      )}
    </section>
  );
}
